import * as React from "react";
import { Export } from "../export";
import Button from "components/form/button";
import { formatMessage } from "lib/globalize-util";
import CircularProgress from "material-ui/CircularProgress";
import { IAllocationDimension, AllocationDimensionKind } from "helpers/allocation-dimension";
import Label from "components/form/label";
import { Form } from "formsy-react";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ExportCollectionSelectedSetting,
    ExportCollectionUnitType,
    ExportLayout,
    EXPORT_STATUS,
} from "constants/product-collection";
import exportStyles, { styles } from "../export-styles";
import { getNameWithDateFormat } from "helpers/dates";

interface IProps {
    collectionName: string;
    allocationDimensions?: IAllocationDimension[];
    collectionExport: any;
    onClose: () => void;
    onExport: (params) => void;
}

export const ExportUpcCollection: React.FC<IProps> = ({
    allocationDimensions = [],
    collectionExport,
    onClose,
    onExport,
    collectionName,
}) => {
    const sizeDimension = React.useMemo(() => {
        return allocationDimensions.find(
            (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Size
        );
    }, [allocationDimensions]);

    const handleExport = () => {
        onExport({
            supplier: "",
            exportOnlyVisibleColumns: ExportCollectionSelectedSetting.Visible,
            exportOnlyVisibleRows: ExportCollectionSelectedSetting.All,
            withImages: false,
            withUnits: false,
            withNotes: false,
            unitType: ExportCollectionUnitType.Actual,
            layout: ExportLayout.Vertical,
            selectedAllocationDimensions: [sizeDimension?.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "")],
            horizontalAllocationDimensions: "",
            exportFileName: getNameWithDateFormat(`UPC-${collectionName}-`).replace(/[^0-9a-z\_\-\.]/gi, "_"),
            isUpcExporting: true,
        });
    };

    return (
        <section style={styles.content}>
            <Export
                error={collectionExport.get("error") || collectionExport.get("status") === EXPORT_STATUS.FAILED}
                isLoading={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.CREATED}
                isReady={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.FINISHED}
            >
                <Export.Selection>
                    <div {...exportStyles}>
                        <div className="formGroupContainer">
                            <p>{formatMessage("Your UPC download will include:")}</p>
                        </div>
                        <Form>
                            <div className="formGroupContainer formUpcContainer">
                                <div>
                                    <Label style={styles.label}>{formatMessage("Rows")}</Label>
                                    <div className="labelValue">{formatMessage("All Rows")}</div>
                                </div>
                                <div>
                                    <Label style={styles.label}>{formatMessage("Columns")}</Label>
                                    <div className="labelValue">
                                        {formatMessage(
                                            "Selected columns, plus the required identifiers for upload and all related size fields."
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="buttonContainer">
                                <Button
                                    label={formatMessage("Cancel")}
                                    onClick={onClose}
                                    style={styles.button.cancel}
                                />
                                <Button
                                    label={formatMessage("Export")}
                                    primary={true}
                                    onClick={handleExport}
                                    style={styles.button.export}
                                />
                            </div>
                        </Form>
                    </div>
                </Export.Selection>
                <Export.Loading>
                    <div {...exportStyles} className="loadingExport">
                        <CircularProgress />
                        <p>{formatMessage("Please wait as we prepare your assortment")}</p>
                    </div>
                </Export.Loading>
                <Export.Ready>
                    <a {...exportStyles} className="downloadExport" href={collectionExport.get("object_url")}>
                        <Button label={formatMessage("Download")} primary={true} large={true} />
                    </a>
                </Export.Ready>
                <Export.Error>
                    <p>{formatMessage("There was an error exporting your assortment.")}</p>
                </Export.Error>
            </Export>
        </section>
    );
};
